import type { PickerOptions } from 'filestack-js';
import { set } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ITTDSubAdvertiser } from '@feathr/blackbox';
import { advertiserConstraints } from '@feathr/blackbox';
import { Form, ImagePicker, Input } from '@feathr/components';
import type { IValidation } from '@feathr/rachis';
import { validate } from '@feathr/rachis';

interface IProps {
  advertiser: ITTDSubAdvertiser;
}

function validateAdvertiser(advertiser: ITTDSubAdvertiser): IValidation<ITTDSubAdvertiser> {
  return validate.validate(advertiser, advertiserConstraints);
}

function EditAdvertiser({ advertiser }: IProps): JSX.Element {
  const store = useLocalObservable(() => ({ dirtyFields: [] as string[] }));
  const { t } = useTranslation();

  function onChangeName(newValue?: string): void {
    if (!store.dirtyFields.includes('name')) {
      store.dirtyFields.push('name');
    }
    set(advertiser, 'name', newValue);
  }

  function onChangeURL(newValue?: string): void {
    if (!store.dirtyFields.includes('url')) {
      store.dirtyFields.push('url');
    }
    set(advertiser, 'url', newValue);
  }

  function onPickLogo(url: string): void {
    if (!store.dirtyFields.includes('logo')) {
      store.dirtyFields.push('logo');
    }
    set(advertiser, 'logo', url);
  }

  const validation = validateAdvertiser(advertiser);

  return (
    <Form label={'Edit Advertiser'}>
      <ImagePicker
        helpText={"File or URL of the organization's logo image. PNG, GIF, or JPG files accepted."}
        label={'Logo'}
        onPick={onPickLogo}
        pickerOptions={
          {
            fromSources: ['local_file_system', 'url'],
            customText: {
              'Select Files to Upload': t('Upload logo file or provide logo URL'),
            },
            maxFiles: 1,
          } as PickerOptions
        }
        validationError={
          validation && store.dirtyFields.includes('logo') ? validation.errors.logo : undefined
        }
        value={advertiser.logo}
      />
      <Input
        helpText={'The name of the business or organization.'}
        label={'Name'}
        onChange={onChangeName}
        type={'text'}
        validationError={
          validation && store.dirtyFields.includes('name') ? validation.errors.name : undefined
        }
        value={advertiser.name}
      />
      <Input
        helpText={'The URL of the main website for the business or organization.'}
        label={'Website URL'}
        onChange={onChangeURL}
        type={'url'}
        validationError={
          validation && store.dirtyFields.includes('url') ? validation.errors.url : undefined
        }
        value={advertiser.url}
      />
    </Form>
  );
}

export default observer(EditAdvertiser);
export { validateAdvertiser };
